import { Button, Icon } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useAuth } from 'hooks';
import { setShowAISearchFeedbackModal } from 'actions/searchNLP';
import styled from 'styled-components';
import { colours } from 'utils/theme';
import { getAiFeedbackSubject } from 'selectors/searchNLP';
import { aiSearchProductName } from 'utils/constants';

const FeedbackButton = styled(Button)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  gap: 8px;
  color: ${colours.character.primary85};

  > span {
    line-height: 1;
    transform: translateY(1px);
  }
`;

export const NLPFeedbackOpenButton = ({ loading, alwaysVisible = false }) => {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const handleShowFeedbackModal = () => {
    dispatch(setShowAISearchFeedbackModal(true));
  };
  // Gate on the feedback subject, not aiSearchData: the subject persists across filter tweaks and
  // re-runs (until Clear), so the feedback button stays available the whole time the user is acting
  // on an AI search rather than vanishing the moment provenance is cleared.
  const feedbackSubject = useSelector(getAiFeedbackSubject);

  // Licence gate first: session state can leak to non-SciAI users (shared AI saved
  // searches, licence downgrades, shared-browser persisted state), and the feedback
  // endpoint is policy-gated server-side anyway — never render the button for them.
  if (!user?.hasSciSearchAI) return null;
  if (!feedbackSubject && !alwaysVisible) return null;
  return (
    <FeedbackButton
      data-testid='nlp-feedback-button'
      type='secondary'
      onClick={handleShowFeedbackModal}
      disabled={loading}
    >
      <Icon icon='sparkles' color={null} size='sm' />
      <span>Provide {aiSearchProductName} Feedback</span>
    </FeedbackButton>
  );
};

// Routes where the header slot in App.js may show the feedback button. Every path
// listed here must mount NLPFeedbackModal (the search page via SearchArea, the
// organisation profile via SciAIOrgSearch) — the click only sets a redux flag, so on
// a page without the modal it silently does nothing.
const MAIN_SEARCH_ROUTE = { path: ['/'], exact: true };
const ORG_PROFILE_ROUTE = { path: '/organisation/:id' };

export const NLPHeaderFeedbackButton = () => {
  const mainSearchMatch = useRouteMatch(MAIN_SEARCH_ROUTE);
  const orgProfileMatch = useRouteMatch(ORG_PROFILE_ROUTE);
  // SCIL-17609: always visible on org profiles so a user can leave general feedback
  // even when idle (no AI search run); main search keeps its subject gate.
  if (orgProfileMatch) return <NLPFeedbackOpenButton alwaysVisible />;
  if (!mainSearchMatch) return null;
  return <NLPFeedbackOpenButton />;
};
